/**
 * Car-vs-car collision — identical CAPSULE colliders for every car, symmetric
 * equal-mass impulse, capped magnitude. Deterministic: same conditions, same
 * result; both the server AND the client's whole-world prediction resolve
 * pairs with this very function, in the same fixed id order.
 *
 * The capsule (segment ±CAR_CAPSULE_HALF_LENGTH along the heading, radius
 * CAR_CAPSULE_RADIUS) is the bumper car's quasi-exact hull (owner's SVG,
 * capsule ±31.5 / r 70.5 in its 144×204 viewBox, max deviation ≈ 0.2 unit)
 * scaled to the 26 px on-track car and rounded to (±4, r 9): sprites touch
 * when the physics touches — the rubber ring is the collider.
 * NOTE: the radius intentionally equals PhysicsConfig.carRadius (9, frozen) so
 * wall contact (track.ts, per capsule end) and car contact use the same ring.
 */

import type { CarState } from './physics';

/**
 * px/s — hard cap on the normal impulse each car can receive from a contact.
 * Raised from 320 with the bumper-car rework (2026-07-21): a full-speed
 * head-on must visibly REBOUND both cars (that's the game), so the cap now
 * allows ≈ one maxSpeed of velocity change — still a hard bound, no pinball.
 */
export const CAR_COLLISION_MAX_IMPULSE = 650;
/** Capsule segment half-length along the heading (px). */
export const CAR_CAPSULE_HALF_LENGTH = 4;
/** Capsule radius (px). Total collider: 26 px long, 18 px wide. */
export const CAR_CAPSULE_RADIUS = 9;

export interface CapsulePose {
  x: number;
  y: number;
  angle: number;
}

interface Segment {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

function capsuleSegment(c: CapsulePose): Segment {
  const hx = Math.cos(c.angle) * CAR_CAPSULE_HALF_LENGTH;
  const hy = Math.sin(c.angle) * CAR_CAPSULE_HALF_LENGTH;
  return { x1: c.x - hx, y1: c.y - hy, x2: c.x + hx, y2: c.y + hy };
}

interface ClosestResult {
  ax: number;
  ay: number;
  bx: number;
  by: number;
  dist: number;
}

/**
 * Closest points between two segments (Ericson, Real-Time Collision Detection
 * §5.1.9) — pure clamped arithmetic, deterministic, robust to degenerate
 * (zero-length, parallel) segments.
 */
function closestSegmentPoints(p: Segment, q: Segment): ClosestResult {
  const d1x = p.x2 - p.x1;
  const d1y = p.y2 - p.y1;
  const d2x = q.x2 - q.x1;
  const d2y = q.y2 - q.y1;
  const rx = p.x1 - q.x1;
  const ry = p.y1 - q.y1;
  const a = d1x * d1x + d1y * d1y;
  const e = d2x * d2x + d2y * d2y;
  const f = d2x * rx + d2y * ry;
  const EPS = 1e-12;

  let s: number;
  let t: number;
  if (a <= EPS && e <= EPS) {
    s = 0;
    t = 0;
  } else if (a <= EPS) {
    s = 0;
    t = clamp01(f / e);
  } else {
    const c = d1x * rx + d1y * ry;
    if (e <= EPS) {
      t = 0;
      s = clamp01(-c / a);
    } else {
      const b = d1x * d2x + d1y * d2y;
      const denom = a * e - b * b;
      s = denom > EPS ? clamp01((b * f - c * e) / denom) : 0;
      t = (b * s + f) / e;
      if (t < 0) {
        t = 0;
        s = clamp01(-c / a);
      } else if (t > 1) {
        t = 1;
        s = clamp01((b - c) / a);
      }
    }
  }

  const ax = p.x1 + d1x * s;
  const ay = p.y1 + d1y * s;
  const bx = q.x1 + d2x * t;
  const by = q.y1 + d2y * t;
  const dx = bx - ax;
  const dy = by - ay;
  return { ax, ay, bx, by, dist: Math.sqrt(dx * dx + dy * dy) };
}

function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}

/** Distance between two car capsule axes (contact when < 2·CAR_CAPSULE_RADIUS). */
export function carCapsuleDistance(a: CapsulePose, b: CapsulePose): number {
  return closestSegmentPoints(capsuleSegment(a), capsuleSegment(b)).dist;
}

interface ContactInfo {
  /** Unit normal pointing from A toward B. */
  nx: number;
  ny: number;
  overlap: number;
}

function capsuleContact(a: CapsulePose, b: CapsulePose): ContactInfo | null {
  const cp = closestSegmentPoints(capsuleSegment(a), capsuleSegment(b));
  const minDist = CAR_CAPSULE_RADIUS * 2;
  if (cp.dist >= minDist) return null;
  let nx: number;
  let ny: number;
  if (cp.dist > 1e-6) {
    nx = (cp.bx - cp.ax) / cp.dist;
    ny = (cp.by - cp.ay) / cp.dist;
  } else {
    // Axes crossing exactly: deterministic fallback on the center axis.
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const d = Math.sqrt(dx * dx + dy * dy);
    if (d > 1e-6) {
      nx = dx / d;
      ny = dy / d;
    } else {
      nx = 1;
      ny = 0;
    }
  }
  return { nx, ny, overlap: minDist - cp.dist };
}

export interface CarCarResult {
  a: CarState;
  b: CarState;
  hit: boolean;
  /** Closing speed along the contact normal (px/s) — impact strength for FX. */
  impact: number;
}

/**
 * Resolves one car-car contact: positional separation (half each) plus a
 * symmetric impulse along the contact normal, restituted with `bounciness`
 * and capped — a contact penalizes but never creates chaotic pinball effects.
 */
export function resolveCarCarCollision(a: CarState, b: CarState, bounciness: number): CarCarResult {
  const contact = capsuleContact(a, b);
  if (!contact) {
    return { a, b, hit: false, impact: 0 };
  }
  const { nx, ny, overlap } = contact;

  // Positional separation, half each — equal treatment by construction.
  const ax = a.x - (nx * overlap) / 2;
  const ay = a.y - (ny * overlap) / 2;
  const bx = b.x + (nx * overlap) / 2;
  const by = b.y + (ny * overlap) / 2;

  // Impulse only if the cars are approaching along the normal.
  const rvn = (b.vx - a.vx) * nx + (b.vy - a.vy) * ny;
  let avx = a.vx;
  let avy = a.vy;
  let bvx = b.vx;
  let bvy = b.vy;
  let impact = 0;
  if (rvn < 0) {
    impact = -rvn;
    // Equal masses: each car receives half of the (1+e)·closing-speed change.
    const j = Math.min(((1 + bounciness) * impact) / 2, CAR_COLLISION_MAX_IMPULSE);
    avx -= nx * j;
    avy -= ny * j;
    bvx += nx * j;
    bvy += ny * j;
  }

  return {
    a: { x: ax, y: ay, angle: a.angle, vx: avx, vy: avy },
    b: { x: bx, y: by, angle: b.angle, vx: bvx, vy: bvy },
    hit: true,
    impact,
  };
}
